import { sanitizeHTML } from '../../utils/safe-html';
import { PermissionControl } from '../../permissions/access';
import { useEffect, useId, useRef, useState } from 'react';
import { Button, Select, Spin, Tag, Typography } from 'antd';
import { getPushPost, isPublishedPost, searchPushPosts } from '../../api/push_posts';

const postSummary = (post) => {
    const title = post.title?.trim();
    if (title) return title;
    // Parse in an inert template, then render text only (never article HTML).
    const template = document.createElement('template');
    template.innerHTML = sanitizeHTML(post.content || '');
    template.content.querySelectorAll('script, style, iframe, template, noscript').forEach((node) => node.remove());
    template.content.querySelectorAll('p, div, br, li, h1, h2, h3, h4, h5, h6').forEach((node) => node.appendChild(document.createTextNode(' ')));
    const content = template.content.textContent.replace(/\s+/g, ' ').trim();
    return content ? `${content.slice(0, 100)}${content.length > 100 ? '…' : ''}` : '无标题或正文';
};
const optionFor = (post) => {
    const kind = String(post.categoryId) === '1' ? 'Post' : 'Article';
    const summary = postSummary(post);
    return {
        value: post.id,
        selectedLabel: `${summary} · ${post.id}`,
        label: <div className="push-post-option"><div><Tag>{kind}</Tag><span>{summary}</span></div><Typography.Text type="secondary">ID：{post.id}{!post.title?.trim() ? ' · 正文摘要' : ''}</Typography.Text></div>,
        post,
    };
};

export default function PostSelect({ value, onChange, disabled }) {
    const helpId = useId();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [matches, setMatches] = useState([]);
    const [status, setStatus] = useState('idle');
    const [selected, setSelected] = useState(null);
    const [detailStatus, setDetailStatus] = useState('idle');
    const [retry, setRetry] = useState(0);
    const version = useRef(0);
    const selectedRef = useRef(null);

    useEffect(() => {
        let active = true;
        if (!value) { selectedRef.current = null; setSelected(null); setDetailStatus('idle'); return; }
        if (selectedRef.current?.id === value) return;
        selectedRef.current = null; setSelected(null); setDetailStatus('loading');
        getPushPost(value).then((post) => {
            if (!active) return;
            selectedRef.current = post; setSelected(post);
            setDetailStatus(isPublishedPost(post) ? 'ready' : 'unavailable');
        }).catch(() => { if (active) setDetailStatus('error'); });
        return () => { active = false; };
    }, [value, retry]);

    useEffect(() => {
        const request = ++version.current;
        if (!open || disabled) { setStatus('idle'); return; }
        setStatus('loading');
        const timer = setTimeout(async () => {
            try {
                const posts = await searchPushPosts(query);
                if (request !== version.current) return;
                setMatches(posts); setStatus(posts.length ? 'ready' : 'empty');
            } catch (error) {
                if (request === version.current) { setMatches([]); setStatus('error'); }
            }
        }, 350);
        return () => { clearTimeout(timer); version.current += 1; };
    }, [query, open, disabled, retry]);

    const changeQuery = (keyword) => {
        version.current += 1;
        setQuery(keyword); setMatches([]); setStatus('loading');
    };
    const choose = (id, option) => {
        version.current += 1;
        selectedRef.current = option?.post || null;
        setSelected(option?.post || null); setDetailStatus(id ? 'ready' : 'idle');
        setQuery(''); setOpen(false); setMatches([]); setStatus('idle');
        onChange(id || '');
    };
    const options = matches.map(optionFor);
    const searchHelp = status === 'loading' ? '正在搜索文章…'
        : status === 'error' ? '文章搜索失败，请重试；若持续失败，请联系管理员检查服务和文章查看权限。'
            : status === 'empty' ? '没有匹配的已发布内容，请换个标题、Post 正文关键词或 ID 片段。'
                : '支持完整或部分 ID、Article / Post 标题及 Post 正文关键词，仅显示已发布内容，最多 10 条。';
    return <div>
        <Select aria-label="搜索文章" aria-describedby={helpId} aria-busy={status === 'loading'} showSearch allowClear virtual={false}
            value={value || undefined} searchValue={query} onSearch={changeQuery} onChange={choose}
            labelRender={() => selected?.id === value ? optionFor(selected).selectedLabel : `ID：${value}`}
            open={open && !disabled} onOpenChange={setOpen} disabled={disabled}
            filterOption={false} optionLabelProp="selectedLabel" options={options} style={{ width: '100%' }}
            placeholder="输入完整或部分文章 ID、标题或 Post 正文关键词" loading={status === 'loading'}
            notFoundContent={status === 'loading' ? <Spin size="small" /> : searchHelp} />
        <div id={helpId} className="push-secondary" role="status">{searchHelp}
            {status === 'error' && <PermissionControl permission="pushcampaigns.control.setOpen.9a73f7cb"><Button type="link" size="small" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => { setOpen(true); setRetry((n) => n + 1); }}>重试搜索</Button></PermissionControl>}
        </div>
        {value && <div className="push-secondary">已选 ID：{value}
            {detailStatus === 'loading' && ' · 正在加载文章信息…'}
            {detailStatus === 'unavailable' && <Typography.Text type="danger"> · 文章不存在或未发布，请重新选择</Typography.Text>}
            {detailStatus === 'error' && <><Typography.Text type="danger"> · 文章信息加载失败，已保留原 ID</Typography.Text><PermissionControl permission="pushcampaigns.control.setRetry.0cf16e92"><Button type="link" size="small" disabled={disabled} onClick={() => setRetry((n) => n + 1)}>重新加载</Button></PermissionControl></>}
        </div>}
    </div>;
}
